/** @jsxImportSource @emotion/react */

import { ChartDataset } from 'chart.js'
import Typography from '@mui/material/Typography'
import { Doughnut } from 'react-chartjs-2'
import { FC, useCallback, useMemo, useState } from 'react'
import { ActiveElement, ChartEvent } from 'chart.js/dist/plugins/plugin.tooltip'
import { mergeDeep } from '@apollo/client/utilities'
import { pluralize } from 'utils/pluralize'
import { convertSecondsToHHMM, Format } from 'utils/convertSecondsToHHMM'
import * as classes from './styles'

const getTotalScheduleTime = (seconds: number) => {
  const hours = Math.floor(seconds / 3600)

  if (hours > 0) {
    return {
      time: Math.round(seconds / 3600),
      type: 'Hour',
    }
  }

  return {
    time: Math.round(seconds / 60),
    type: 'Minute',
  }
}

type Props = {
  datasets: ChartDataset<'doughnut', number[]>[]
  totalTime: number
  listData: Array<{
    color: string
    name: string
    time: number
  }>
}

export const ChartCardContent: FC<Props> = ({ datasets, totalTime, listData }) => {
  const [hoveredSegmentIndex, setHoveredSegmentIndex] = useState<number | null>(null)

  const handleHover = useCallback(
    (_: ChartEvent, elements: ActiveElement[]) => {
      if (elements.length > 0) {
        const index = elements[0].index
        setHoveredSegmentIndex(index)
      } else {
        setHoveredSegmentIndex(null)
      }
    },
    [setHoveredSegmentIndex]
  )

  const data = useMemo(
    () => ({
      labels: [],
      datasets: mergeDeep(
        [
          {
            borderWidth: 3,
            borderRadius: 5,
            offset: 2,
            hoverBorderColor: 'transparent',
            borderJoinStyle: 'round',
          },
        ],
        datasets
      ),
    }),
    [datasets]
  )

  const options = useMemo(
    () => ({
      cutout: '60%',
      plugins: {
        tooltip: {
          enabled: false,
        },
        legend: {
          display: false,
        },
      },
      onHover: handleHover,
    }),
    [handleHover]
  )

  return (
    <>
      <div css={classes.chartWrapper}>
        <Doughnut data={data} options={options} />
        <div css={classes.centerText} data-testid="chart-center-text">
          <Typography css={classes.hours} data-testid="chart-total-time">
            {getTotalScheduleTime(totalTime).time}
          </Typography>
          <Typography css={classes.hoursLabel} data-testid="chart-time-unit">
            {pluralize(getTotalScheduleTime(totalTime).time, getTotalScheduleTime(totalTime).type)}
          </Typography>
        </div>
      </div>

      <ul css={classes.ul} data-testid="chart-list">
        {listData.map((item, index) => (
          <li
            key={item.name}
            css={classes.li}
            data-testid={`chart-item-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <div css={classes.subjectName}>
              <div css={classes.dotWrapper}>
                <div css={classes.dot(item.color, index === hoveredSegmentIndex)} />
              </div>
              <Typography css={classes.subjectNameTypography} data-testid="chart-item-name">
                {item.name}
              </Typography>
            </div>
            <div>
              <Typography css={classes.time} data-testid="chart-item-time">
                {convertSecondsToHHMM(item.time, Format.Full)}
              </Typography>
            </div>
          </li>
        ))}
      </ul>
    </>
  )
}
